

import { useTranslation } from "react-i18next";
import { Button, Table, TableContainer, TableHead, TableRow, TableCell, Checkbox, TableBody, Paper, MenuList, ListItemIcon, ListItemText, Tooltip, Snackbar, Alert, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CommonGrid from "../CommonGrid";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import ContextMenu from "../../ContextMenu/ContextMenu";
import { MenuItem } from "semantic-ui-react";
import { ContentCopy } from "@mui/icons-material";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import ConfirmModal from "../../ItemProperties/Forms/FieldDataValidation/ConfirmModal";
import TableLoading from "../../Save/TableLoading";
import LightTooltip from "../../../commonComponents/LightTooltip";

export default function DataDepotFieldsTable({ 
  dataDepotFieldList,
  setDataDepotFieldList,
  actionQueue,
  setActionQueue,
  dataDepotSelected,
  fieldDataTypes,
  ddFieldTableOverlayText,
  validateFieldName
}) {
  const  { t } = useTranslation();
  const [gridApi, setGridApi] = useState();
  const [editDdfErrorModalOpen, setEditDdfErrorModalOpen] = useState(false);
  const [editDdfErrorMsg, setEditDdfErrorMsg] = useState(t('DATA_DEPOTS_MUST_CONTAIN_ATLEAST_ONE_FIELD'));
  const [deleteErrorModal, setDeleteErrorModal] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [validateFieldDeleteData, setValidateFieldDeleteData] = useState(undefined);

  const logFieldAction = (action, data, newVal) => {
    switch(action) {
      case 'updateField':
        setActionQueue(prev => [...prev, {
          type: 'updateDataDepotField',
          params: {
            depotName: dataDepotSelected,
            fieldName: data.field_name,
            fieldType: data.data_type,
            newName: newVal,
            newType: data.data_type
          }
        }])
        break;
      case 'deleteField':
        setActionQueue(prev => [...prev, {
          type: 'deleteDataDepotField',
          params: {
            depotName: dataDepotSelected,
            fieldName: data.field_name,
          }
        }])
        break;
    }
  }

  useEffect(() => {
    if (validateFieldDeleteData) {
      handleDeleteClick(validateFieldDeleteData, dataDepotFieldList)
      setValidateFieldDeleteData(false);
    }
  }, [validateFieldDeleteData])

  const handleDeleteClick = (data, list) => {
    if (!list) setValidateFieldDeleteData(data);
    else {
      if (list.length > 1) {
        //remove from list, then log action
        setDataDepotFieldList(prev => prev.filter(o => o.field_name !== data.field_name));
        logFieldAction('deleteField', data);
      } else {
        //user feedback displaying that there must be atleast one field in data depot
        setDeleteErrorMsg(t('DATA_DEPOTS_MUST_CONTAIN_ATLEAST_ONE_FIELD'));
        setDeleteErrorModal(true);
      }
    }
  };

  const CustomDropdownEditor = ({ value, setValue, values, data }) => {
    const selectRef = useRef(null);
    const {t} = useTranslation();
    const [dtVal, setDtVal] = useState('SELECT');

    useEffect(() => {
      setDtVal(data?.data_type || 'SELECT');
    }, []);

    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: '2rem',
      }}>
        <div>{data?.data_type ? t(data.data_type) : t("ALPHA")}</div>
        <LightTooltip
          title={t('DELETE')}
          arrow
          data-type="row-el"
        >
          <span style={{ cursor: 'pointer', fontSize: '22px' }} className="DELETE" onClick={() => handleDeleteClick(data)}></span>
        </LightTooltip>
      </div>
    );
  };

  const gridOptions = {
    columnDefs : [
      {
        headerName: t("TBL_FLD_NAME"),
        field: "field_name",
        editable: true,
        resizable: true,
      },
      {
        headerName: t("DATA_TYPE"),
        resizable: false,
        editable: false,
        cellRenderer: CustomDropdownEditor,
        // width: 305,
        cellStyle: {paddingRight: "8px", borderRight: 'none'},
      },
    ],
    suppressHorizontalScroll: false,
  }

  //triggers when cell value changes. runs before edit stop.
  const handleCellValueChanged = (e) => {
    if (e.newValue !== "") {
      validateFieldName(e.newValue, (errMsg) => {
        if (errMsg) {
          resetFieldList(e.newValue, e.oldValue, e.rowIndex);
          setEditDdfErrorMsg(t(errMsg));
          setEditDdfErrorModalOpen(true);
        } else {
          if ((e.oldValue && e.newValue) && e.oldValue !== e.newValue) {
            let node = {
              data_type: e.data.data_type,
              field_name: e.oldValue
            };
            logFieldAction('updateField', node, e.newValue)
          }
        }
      }, true);
    }
  }

  //triggers when close button is pressed in ErrorModal
  const handleEditDdfErrorModalClose = () => {
    setEditDdfErrorModalOpen(false);
  }

  //triggers on cell focus
  const cellFocus = (e) => {
    // console.log(e);
  }

  //find a node, and set it's value to its previous value before edit.
  const resetFieldList = (newVal, oldVal, index) => {
    setDataDepotFieldList(prev => prev.map((o, i) => {
      if (i === index) {
        return {
          id: o.id,
          key: o.key,
          field_name: oldVal,
          data_type: o.data_type,
          value: o.value
        }
      } else return o;
    }));
  }

  const handleCellClick = (options) => {
    //on cell click, will trigger cell edit
    if (options.value) {
      //timeout to prevent race condition with grid api init
      setTimeout(() => {
        options.api.startEditingCell({
          rowIndex: options.rowIndex,
          colKey: options.column.colId,
        });
      });
    }
  }

  return (
    <div id="ddFieldTableContainer" style={styles.fieldTableContainer}>
      <CommonGrid 
        rowData={dataDepotFieldList} 
        gridOptions={gridOptions}
        containerStyle={styles.gridStyle}
        getGridAPI={(gridOptions, gridApi) => {
          setGridApi(gridApi.api)
        }}
        gridProps={{
          cellFocus: cellFocus,
          suppressScrollOnNewData: true,
          suppressScrollWhenPopupsAreOpen: true,
          frameworkComponents: {
            customDropdownEditor: CustomDropdownEditor,
          },
          animateRows: false,
          onCellValueChanged: handleCellValueChanged,
          onCellClicked: handleCellClick,
          disableNewRowOnCellValueChange: true,
          enableCellTextSelection: true,
          ensureDomOrder: true,
          suppressClickEdit: true,
          overlayNoRowsTemplate: `<span>${ddFieldTableOverlayText}</span>`, 
          singleClickEdit: true,
        }}
      />
      <ErrorModal
        errorModal={deleteErrorModal}
        setErrorModal={setDeleteErrorModal}
        errorMessage={deleteErrorMsg}
        handleErrorModal={setDeleteErrorModal}
      />
      <ErrorModal
        errorModal={editDdfErrorModalOpen}
        setErrorModal={setEditDdfErrorModalOpen}
        errorMessage={editDdfErrorMsg}
        handleErrorModal={setEditDdfErrorModalOpen}
        closeHandler={handleEditDdfErrorModalClose}
        OkHandler={handleEditDdfErrorModalClose}
      />
    </div>
  )
}

const styles = {
  gridStyle: {
    height: "100%",
    overflowX: "none", 
  },
  fieldTableContainer: {
    height: '100%',
    width: '100%'
  },
  rowText: { overflow: 'hidden' },
}

